import React from 'react';
import Table from 'antd/lib/table';
import Popover from 'antd/lib/popover';
import Typography from 'antd/lib/typography';
import { ColumnsType } from 'antd/lib/table/interface';
import { InfoCircleOutlined } from '@ant-design/icons';
import { AsyncJobContextSections, AsyncJobStatus, IAsyncJob } from '../../../domain/async-job/interface';
import { PRIMARY_COLOR } from '../../../theme';
import { WithEmptyDataTable } from '../../table/withEmptyDataTable';
import { getComputerFormattedTime } from '../../../domain/date/date';

interface IAsyncJobsModalProps {
    asyncJobs: IAsyncJob[];
    isLoading?: boolean;
    limit?: number;
}

const STATUS_COLOR_MAP: { [key: string]: string } = {
    [AsyncJobStatus.PENDING]: '#888787',
    [AsyncJobStatus.IN_PROGRESS]: '#fac42a',
    [AsyncJobStatus.SUCCESS]: '#26ad5f',
    [AsyncJobStatus.FAILED]: '#fa675c',
};

const STATUS_TEXT_MAP: { [key: keyof typeof STATUS_COLOR_MAP]: string } = {
    [AsyncJobStatus.PENDING]: 'Pending',
    [AsyncJobStatus.IN_PROGRESS]: 'In Progress',
    [AsyncJobStatus.SUCCESS]: 'Success',
    [AsyncJobStatus.FAILED]: 'Failed',
};

export const AsyncJobsModalContent: React.FC<IAsyncJobsModalProps> = ({ limit = 10, isLoading, asyncJobs }) => {
    const columns: ColumnsType<IAsyncJob> = [
        {
            title: 'Start Date',
            dataIndex: 'started_at',
            render: (startedAt: string) => (!startedAt ? '-' : getComputerFormattedTime(startedAt)),
        },
        {
            title: 'End Date',
            dataIndex: 'finished_at',
            render: (finishedAt: string) => (!finishedAt ? '-' : getComputerFormattedTime(finishedAt)),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (status: string) => <span style={{ color: STATUS_COLOR_MAP[status] }}>{STATUS_TEXT_MAP[status]}</span>,
        },
        {
            title: 'Requested By',
            dataIndex: 'requested_by',
        },
        {
            title: 'Created Date',
            dataIndex: 'created_at',
            render: (createdAt: string) => getComputerFormattedTime(createdAt),
        },
        {
            title: 'Error Message',
            dataIndex: ['context', 'error'], // context?.error
            width: 240,
            render: (error: string) =>
                error ? (
                    <Typography.Paragraph ellipsis={{ rows: 3, tooltip: error }} style={{ margin: 0 }}>
                        {error}
                    </Typography.Paragraph>
                ) : (
                    '-'
                ),
        },
        {
            title: 'Details',
            dataIndex: 'context',
            render: (context: { [key: string]: string } | null) => {
                const hasDetails = !!context && Object.keys(context).some(key => key !== 'error');
                return hasDetails ? <AsyncJobDetailsPopover context={context} /> : '-';
            },
        },
    ];

    return (
        <>
            <WithEmptyDataTable condition={!!isLoading}>
                <Table rowKey="request_id" columns={columns} dataSource={asyncJobs} pagination={false} loading={isLoading} tableLayout="fixed" />
            </WithEmptyDataTable>

            <div style={{ padding: '8px 0' }}>
                <Typography.Text>*Shows the last {limit} operations, updates every minute</Typography.Text>
            </div>
        </>
    );
};

const AsyncJobDetailsPopover: React.FC<{ context: { [key: string]: string } }> = ({ context }) => {
    const content = (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {AsyncJobContextSections.map(section => {
                const sectionEntries = section.keys.filter(({ key }) => key in context);
                if (sectionEntries.length === 0) return null;

                return (
                    <div key={section.title} style={{ marginBottom: 12 }}>
                        <Typography.Text strong style={{ fontSize: 13 }}>
                            {section.title}
                        </Typography.Text>
                        {sectionEntries.map(({ key, label }) => (
                            <p key={key} style={{ margin: '2px 0', paddingLeft: 8 }}>
                                {label}: {String(context[key])}
                            </p>
                        ))}
                    </div>
                );
            })}
        </div>
    );

    return (
        <Popover content={content} title="Details" styles={{ root: { width: 350 } }}>
            <InfoCircleOutlined style={{ color: PRIMARY_COLOR, fontSize: '16px', paddingLeft: '8px' }} />
        </Popover>
    );
};
